/**
 * WolfeCaseStudyPage — Pełna strona case study /historie-sukcesu/wolfe-vom-katzenberg
 *
 * Filozofia: realny proof-of-concept APISAI. Realne screeny, prawdziwa historia.
 * Spec: 01_MASTER_SPEC_LANDING_PAGES.md §7
 *
 * Flow: Problem → Rozwiązanie → Strona premium → Katalog/Widoczność → Promocja → Zapytania
 */
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  ArrowRight, ArrowLeft, ShieldCheck, Globe, Search, MessageSquare,
  Megaphone, MapPin, CheckCircle2, Sparkles,
} from 'lucide-react';

export default function WolfeCaseStudyPage() {
  return (
    <div className="min-h-screen bg-[#070710] text-white" data-testid="wolfe-case-study-page">
      <Helmet>
        <title>Wolfe vom Katzenberg — Historia sukcesu APISAI</title>
        <meta name="description" content="Jak hodowla Wolfe vom Katzenberg wykorzystuje APISAI do budowania profesjonalnej obecności online, widoczności w katalogu i zdobywania zapytań od klientów." />
        <link rel="canonical" href="https://apisai.pl/historie-sukcesu/wolfe-vom-katzenberg" />
      </Helmet>

      {/* Ambient */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-950/20 via-[#070710] to-[#070710]" />
      </div>

      {/* Top minibar */}
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition" data-testid="case-back-home">
          <ArrowLeft className="w-4 h-4" /> Wróć na stronę główną
        </Link>
      </div>

      {/* Hero */}
      <section className="relative pt-10 pb-14 sm:pt-14 sm:pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-xs font-medium mb-5">
            <ShieldCheck className="w-3.5 h-3.5" /> Realny case study
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="case-headline">
            Wolfe vom Katzenberg.<br />
            <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
              Hodowla, która wygląda jak premium marka online.
            </span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-zinc-400 leading-relaxed max-w-2xl">
            Wolfe vom Katzenberg to przykład hodowli, która używa APISAI, by łączyć profesjonalną stronę, plany hodowlane, widoczność w katalogu i kanały promocji w jeden spójny system.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-zinc-500">
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-orange-300" /> Warszawa, PL</span>
            <span className="inline-flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-violet-300" /> Strona premium · plany hodowlane</span>
            <span className="inline-flex items-center gap-1.5"><Search className="w-3.5 h-3.5 text-emerald-300" /> Katalog APISAI + Google</span>
          </div>
        </div>
      </section>

      {/* Flow */}
      <section className="relative pb-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-5 gap-3 sm:gap-4">
            {[
              { t: 'Problem', d: 'Brak nowoczesnej obecności online.', color: 'text-red-300', bg: 'bg-red-500/10', border: 'border-red-500/25' },
              { t: 'Rozwiązanie', d: 'Strona premium na APISAI.', color: 'text-orange-300', bg: 'bg-orange-500/10', border: 'border-orange-500/25' },
              { t: 'Plany hodowlane', d: 'Transparentność miotów i reproduktorów.', color: 'text-amber-300', bg: 'bg-amber-500/10', border: 'border-amber-500/25' },
              { t: 'Widoczność', d: 'Katalog APISAI + Google + Bing.', color: 'text-violet-300', bg: 'bg-violet-500/10', border: 'border-violet-500/25' },
              { t: 'Zapytania', d: 'Wiadomości od przyszłych właścicieli.', color: 'text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-500/25' },
            ].map((s, i) => (
              <div key={s.t} className={`rounded-2xl p-4 ${s.bg} border ${s.border}`}>
                <div className={`text-[10px] uppercase tracking-wider font-semibold ${s.color} mb-1`}>Krok {i + 1}</div>
                <div className="text-base font-bold text-white">{s.t}</div>
                <div className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real screen #1 — Homepage */}
      <section className="relative py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/25 text-orange-300 text-xs font-medium mb-3">
                <Globe className="w-3.5 h-3.5" /> Strona premium
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Profesjonalna strona, która buduje zaufanie</h2>
              <p className="mt-4 text-zinc-400 leading-relaxed text-sm sm:text-base">
                Klienci szukający szczeniąt nie chcą pdfów ani grup na FB. Oczekują strony, która wygląda jak marka. Wolfe vom Katzenberg ma taką stronę — z hero, opisem, planami hodowlanymi i wyraźnym CTA.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-zinc-300">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" /> Spójna identyfikacja wizualna</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" /> Sekcje O hodowli / Reproduktory / Plany</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" /> Premium dark theme + wysoka jakość zdjęć</li>
              </ul>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="lg:col-span-3 group relative rounded-2xl overflow-hidden border border-white/10 bg-[#0c0c1a] shadow-2xl shadow-black/40">
              <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] font-medium text-emerald-300 border border-emerald-500/30">
                Realny screen · Homepage
              </div>
              <img src="/landing-spec/wolfe/wolfe_home_real_screenshot.png" alt="Wolfe vom Katzenberg homepage"
                className="w-full h-auto block" loading="lazy" data-testid="case-screen-home" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Real screen #2 — Plany hodowlane */}
      <section className="relative py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-5 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="lg:col-span-3 order-2 lg:order-1 group relative rounded-2xl overflow-hidden border border-white/10 bg-[#0c0c1a] shadow-2xl shadow-black/40">
              <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] font-medium text-violet-300 border border-violet-500/30">
                Realny screen · Plany hodowlane
              </div>
              <img src="/landing-spec/wolfe/wolfe_plany_hodowlane_real_screenshot.png" alt="Wolfe vom Katzenberg – plany hodowlane"
                className="w-full h-auto block" loading="lazy" data-testid="case-screen-plans" />
            </motion.div>
            <div className="lg:col-span-2 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/25 text-violet-300 text-xs font-medium mb-3">
                <Sparkles className="w-3.5 h-3.5" /> Plany hodowlane
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Pełna transparentność miotów i krycia</h2>
              <p className="mt-4 text-zinc-400 leading-relaxed text-sm sm:text-base">
                APISAI pozwala publikować plany hodowlane: krycia, mioty, status dostępności i informacje praktyczne. To buduje wiarygodność i przyspiesza decyzje przyszłych właścicieli.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-zinc-300">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" /> Status: planowane / dostępne / rezerwacja</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" /> Rodzice + zdjęcia</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" /> CTA: Zapytaj o szczenię</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Visibility ecosystem block */}
      <section className="relative py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-violet-950/40 via-[#0a0a16] to-[#0a0a16] border border-white/10 rounded-3xl p-6 sm:p-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-xs font-medium mb-3">
              <Search className="w-3.5 h-3.5" /> Widoczność online
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Klienci mogą znaleźć hodowlę w wielu miejscach</h2>
            <p className="mt-3 text-zinc-400 leading-relaxed text-sm sm:text-base max-w-2xl">
              Profil Wolfe vom Katzenberg jest widoczny w katalogu APISAI i wyszukiwarkach, dzięki czemu klienci mogą trafić na hodowlę z różnych źródeł.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 sm:gap-3">
              {['Google', 'Mapy Google', 'Bing', 'Facebook', 'Instagram', 'Katalog APISAI', 'WhatsApp', 'AI Search'].map((s) => (
                <span key={s} className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.10] text-zinc-300 text-xs">{s}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Promo + Messages */}
      <section className="relative py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-5 sm:gap-6">
          <div className="rounded-2xl p-6 border border-white/10 bg-[#0c0c1a]">
            <div className="w-11 h-11 rounded-xl bg-pink-500/15 border border-pink-500/30 flex items-center justify-center mb-4">
              <Megaphone className="w-5 h-5 text-pink-300" />
            </div>
            <h3 className="text-lg font-bold">Promocja postów</h3>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
              Każdy nowy miot lub plan hodowlany może zostać opublikowany jako post w katalogu APISAI i kanałach społecznościowych.
            </p>
          </div>
          <div className="rounded-2xl p-6 border border-white/10 bg-[#0c0c1a]">
            <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-4">
              <MessageSquare className="w-5 h-5 text-emerald-300" />
            </div>
            <h3 className="text-lg font-bold">Zapytania w jednym miejscu</h3>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
              Wszystkie wiadomości od potencjalnych właścicieli trafiają do panelu APISAI — bez rozproszenia po FB, mailu i WhatsAppie.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Zbuduj swój ekosystem widoczności</h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            APISAI działa dla hodowli, groomerów, restauracji, barów i lokalnych usług. Zacznij za darmo.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/rejestracja"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-orange-500 hover:bg-orange-400 text-white rounded-xl font-semibold shadow-2xl shadow-orange-500/30 transition"
              data-testid="case-final-cta">
              Załóż profil za darmo <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/hodowle"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-white/15 hover:border-orange-500/40 text-zinc-200 font-semibold transition">
              Zobacz katalog hodowli
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
